import React from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FaqSection from "@/components/FaqSection";
import { motion } from "framer-motion";
import { Award, Star } from "lucide-react";

export default function OurStory() {
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main id="main-content" className="pt-32 pb-16 px-6" tabIndex="-1" style={{ scrollMarginTop: "80px" }}>
        <div className="max-w-4xl mx-auto">

          {/* H1 */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <p className="font-accent text-sm tracking-[0.4em] uppercase text-primary mb-4" lang="it">La Nostra Storia</p>
            <h1 className="font-display text-4xl md:text-6xl text-foreground mb-8 leading-tight">
              Our Story: Cousins{" "}
              <span className="italic text-primary">Salvatore Lima</span>
              {" "}<br />and Enzo Auriuso
            </h1>
          </motion.div>

          {/* Mural image */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative mb-10"
          >
            <div className="overflow-hidden">
              <img
                src="https://base44.app/api/apps/69f43144627ee540dc204e12/files/mp/public/69f43144627ee540dc204e12/b4219568d_86f43131b_17C9CC1A-AA5C-4856-BAB4-258A6823940C-1024.webp"
                srcSet="https://base44.app/api/apps/69f43144627ee540dc204e12/files/mp/public/69f43144627ee540dc204e12/b4219568d_86f43131b_17C9CC1A-AA5C-4856-BAB4-258A6823940C-1024.webp 480w, https://base44.app/api/apps/69f43144627ee540dc204e12/files/mp/public/69f43144627ee540dc204e12/b4219568d_86f43131b_17C9CC1A-AA5C-4856-BAB4-258A6823940C-1024.webp 768w, https://base44.app/api/apps/69f43144627ee540dc204e12/files/mp/public/69f43144627ee540dc204e12/b4219568d_86f43131b_17C9CC1A-AA5C-4856-BAB4-258A6823940C-1024.webp 1024w"
                alt="The A' Cucina e Mamma mural painted on the dining room wall at Donna Margherita"
                className="w-full h-auto"
                loading="lazy"
                decoding="async"
                width="800"
                height="600"
                sizes="(max-width: 768px) 100vw, 896px"
              />
            </div>
          </motion.div>

          {/* H2: From Naples */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="mb-10"
          >
            <h2 className="font-display text-3xl md:text-4xl text-foreground mb-6">
              From Naples to the Upper East Side
            </h2>
            <div className="space-y-5 font-body text-muted-foreground leading-relaxed text-[15px]">
              <p>
                Donna Margherita, the best Neapolitan restaurant in the Upper East Side for authentic Neapolitan pizza in the Upper East Side, is run by cousins Salvatore Lima and Enzo Auriuso. Born in Naples and raised around family tables overflowing with food and laughter, both cousins grew up learning from the women who cooked with love and memory rather than recipe books.
              </p>
              <p>
                After building careers in the hospitality industry across Italy and New York, Salvatore and Enzo decided to bring what they missed most, real Neapolitan food, to Manhattan. They opened Donna Margherita on Second Avenue and haven't looked back. Donna Margherita opened on the Upper East Side in 2017.
              </p>
              <p>
                Today, Donna Margherita is recognized as one of the best Neapolitan restaurants on the Upper East Side. Guests come for the brick oven pizza, stay for the Genovese, and leave feeling like they've just eaten at someone's home in Naples.
              </p>
            </div>
          </motion.section>

          {/* H2: Mamma's Recipes */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="mb-10"
          >
            <h2 className="font-display text-3xl md:text-4xl text-foreground mb-6">
              Bringing Mammà's Recipes to the Table
            </h2>
            <div className="space-y-5 font-body text-muted-foreground leading-relaxed text-[15px]">
              <p>
                The menu is built around the dishes both cousins grew up eating. Their mothers' recipes, handed down without measurements and written in instinct rather than ink, are the foundation of everything served at Donna Margherita.
              </p>
              <p>
                Ingredients are imported directly from Italy: San Marzano tomatoes, mozzarella, prosciutto di Parma, Italian wines. The brick oven, central to every Neapolitan kitchen, fires every pizza at the same temperatures used in Naples for generations.
              </p>
              <p>
                The Neapolitan Genovese, pappardelle pasta with celery, carrots, onion, and short ribs braised for seven hours, is the dish guests come back for again and again. It is slow, patient cooking that cannot be rushed.
              </p>
            </div>
          </motion.section>

          {/* H2: A' Cucina */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="mb-10"
          >
            <h2 className="font-display text-3xl md:text-4xl text-foreground mb-6">
              The <span lang="it">A' Cucina 'e Mammà</span> Experience
            </h2>
            <div className="space-y-5 font-body text-muted-foreground leading-relaxed text-[15px]">
              <p>
                "<span lang="it">A' Cucina 'e Mammà</span>", the kitchen of mother, is more than a phrase painted on the wall. It is the philosophy of the restaurant. Dining at Donna Margherita should feel the way it feels to eat at home in Naples: abundant, unhurried, and warm.
              </p>
              <p>
                Tables are meant to be lingered at. Meals are meant to be shared. The staff treats every guest as an extension of the family. This is the Neapolitan way, and it is the standard Salvatore and Enzo hold themselves to every service.
              </p>
            </div>
          </motion.section>

          {/* H2: Awards */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="mb-10 py-10 border-y border-border/40"
          >
            <h2 className="font-display text-3xl md:text-4xl text-foreground mb-10 text-center">
              Award Winning Neapolitan Cuisine
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <Award className="w-8 h-8 text-primary mx-auto mb-3" />
                <h3 className="font-display text-lg text-foreground mb-1">Gambero Rosso Two Forks, Three Times</h3>
                <p className="font-body text-sm text-muted-foreground">Awarded 2024, 2025, and 2026, one of the highest distinctions for Italian restaurants worldwide.</p>
              </div>
              <div className="text-center">
                <Star className="w-8 h-8 text-primary mx-auto mb-3" />
                <h3 className="font-display text-lg text-foreground mb-1">Ospitalità Italiana Seal</h3>
                <p className="font-body text-sm text-muted-foreground">Recognized for quality and authenticity by the Italian Chamber of Commerce.</p>
              </div>
              <div className="text-center">
                <Award className="w-8 h-8 text-primary mx-auto mb-3" />
                <h3 className="font-display text-lg text-foreground mb-1">Accademia Italiana della Cucina</h3>
                <p className="font-body text-sm text-muted-foreground">Recognized by Italy's most prestigious culinary academy for upholding Neapolitan food traditions.</p>
              </div>
            </div>

            {/* Gambero Rosso ceremony photo */}
            <div className="mt-10 overflow-hidden">
              <img
                src="https://base44.app/api/apps/69f43144627ee540dc204e12/files/mp/public/69f43144627ee540dc204e12/5663c3c65_fb2d03eae_Screenshot2026-06-19at33044AM-1024.webp"
                srcSet="https://base44.app/api/apps/69f43144627ee540dc204e12/files/mp/public/69f43144627ee540dc204e12/5663c3c65_fb2d03eae_Screenshot2026-06-19at33044AM-1024.webp 480w, https://base44.app/api/apps/69f43144627ee540dc204e12/files/mp/public/69f43144627ee540dc204e12/5663c3c65_fb2d03eae_Screenshot2026-06-19at33044AM-1024.webp 768w, https://base44.app/api/apps/69f43144627ee540dc204e12/files/mp/public/69f43144627ee540dc204e12/5663c3c65_fb2d03eae_Screenshot2026-06-19at33044AM-1024.webp 1024w"
                alt="Salvatore Lima and Enzo Auriuso receiving the Gambero Rosso Top Italian Restaurants award at Metropolitan Pavilion, New York"
                className="w-full h-auto"
                loading="lazy"
                decoding="async"
                width="896"
                height="600"
                sizes="(max-width: 768px) 100vw, 896px"
              />
              <p className="font-body text-xs text-muted-foreground text-center py-3 bg-card">
                Receiving the Gambero Rosso Top Italian Restaurants award · Metropolitan Pavilion, New York
              </p>
            </div>

            {/* Award logos */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
              {[
                { src: "https://media.base44.com/images/public/69f43144627ee540dc204e12/7f320d60b_FF8C5501-6FAA-4F7B-9C2D-9DFC5DCB6D64.png", alt: "Gambero Rosso Top Italian Restaurants 2024" },
                { src: "https://media.base44.com/images/public/69f43144627ee540dc204e12/62df05557_2401A2C2-1E8F-4769-B36B-2F472217F794.png", alt: "Gambero Rosso Top Italian Restaurants 2025" },
                { src: "https://media.base44.com/images/public/69f43144627ee540dc204e12/12e68038a_6AB19802-6EA6-4574-AA06-6F1BA81A9A62.png", alt: "Gambero Rosso Top Italian Restaurants 2026" },
                { src: "https://media.base44.com/images/public/69f43144627ee540dc204e12/afd51d43c_401C00C7-A763-4CE1-940E-3FF39AA46439.png", alt: "Ospitalità Italiana Quality Approved" },
              ].map((award, i) => (
               <div key={i} className="overflow-hidden rounded-lg shadow-sm border border-border/20 bg-card/50">
                 <img
                   src={`${award.src}?w=600&q=85&fm=webp`}
                   srcSet={`${award.src}?w=300&q=85&fm=webp 300w, ${award.src}?w=600&q=85&fm=webp 600w, ${award.src}?w=900&q=85&fm=webp 900w`}
                   sizes="(max-width: 768px) 50vw, 220px"
                   alt={award.alt}
                   className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                   loading="lazy"
                   decoding="async"
                   width="300"
                   height="300"
                 />
               </div>
              ))}
            </div>
          </motion.section>

          {/* H2: Why You'll Feel at Home */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="mb-10"
          >
            <h2 className="font-display text-3xl md:text-4xl text-foreground mb-6">
              Why You'll Feel at Home
            </h2>
            <div className="space-y-5 font-body text-muted-foreground leading-relaxed text-[15px]">
              <p>
New York has hundreds of Italian restaurants, and the Upper East Side Italian restaurants alone number in the dozens, but only a handful are Upper East Side Neapolitan restaurants, and very few of them feel like the ones in Italy. Donna Margherita does. The room is warm. The food is generous. The owners know your name. Regulars sit at the same table every week. First-timers are welcomed the same way.
              </p>
              <p>
                This is not a performance of Italian culture. It is a genuine extension of it, carried across the Atlantic by two cousins who grew up inside it and could not imagine offering anything less.
              </p>
            </div>

            {/* Celebrity guests photo */}
            <div className="mt-10 overflow-hidden">
              <img
                src="https://base44.app/api/apps/69f43144627ee540dc204e12/files/mp/public/69f43144627ee540dc204e12/9d696546f_c5d827abe_Screenshot2026-06-19at33106AM-1024.webp"
                srcSet="https://base44.app/api/apps/69f43144627ee540dc204e12/files/mp/public/69f43144627ee540dc204e12/9d696546f_c5d827abe_Screenshot2026-06-19at33106AM-1024.webp 480w, https://base44.app/api/apps/69f43144627ee540dc204e12/files/mp/public/69f43144627ee540dc204e12/9d696546f_c5d827abe_Screenshot2026-06-19at33106AM-1024.webp 768w, https://base44.app/api/apps/69f43144627ee540dc204e12/files/mp/public/69f43144627ee540dc204e12/9d696546f_c5d827abe_Screenshot2026-06-19at33106AM-1024.webp 1024w"
                alt="Gigi D'Alessio at Donna Margherita during a Benvenuto a New York welcome"
                className="w-full h-auto"
                loading="lazy"
                decoding="async"
                width="896"
                height="600"
                sizes="(max-width: 768px) 100vw, 896px"
              />
              <p className="font-body text-xs text-muted-foreground text-center py-3 bg-card">
                Welcoming Gigi D'Alessio to Donna Margherita: Benvenuto a New York
              </p>
            </div>

            <FaqSection path="/our-story" compact />
          </motion.section>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="flex flex-col sm:flex-row gap-4"
          >
            <a
              href="https://www.opentable.com/r/donna-margherita-new-york"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Reserve a table at Donna Margherita on OpenTable (opens in a new tab)"
              className="font-body text-sm font-medium rounded-full px-10 py-4 bg-primary text-primary-foreground hover:bg-primary/90 active:scale-95 transition-all duration-200 shadow-md text-center"
            >
              Reserve a Table
            </a>
            <Link
              to="/menu"
              className="font-body text-sm font-medium rounded-full px-10 py-4 bg-secondary text-secondary-foreground hover:bg-secondary/90 transition-all duration-300 shadow-md text-center"
            >
              View Our Menu
            </Link>
          </motion.div>
        </div>
      </main>
      <Footer />
    </div>
  );
}